// The trader page's arithmetic, outside the component because this project's
// vitest has no React renderer: anything left inside a component cannot be
// tested at all, and "which of these numbers is the biggest gap" is exactly the
// kind of thing that is wrong quietly.
import type { TraderItemDetail } from "../api/types";
import { byText } from "./compare";

export type TraderSort = "item" | "mode" | "vsMarket" | "moved";

/** How far this trader sits from the scan's best. Zero means they set it —
 *  a fact, not a missing value, which is why it is not folded into null. */
export function vsMarket(row: TraderItemDetail): number | null {
  if (row.theirs === null || row.market_best === null) return null;
  return row.theirs - row.market_best;
}

/** What their own price did since they last offered this item. */
export function moved(row: TraderItemDetail): number | null {
  if (row.theirs === null || row.previous === null) return null;
  return row.theirs - row.previous;
}

/** Ranked by distance from zero, because "how far off" is the question both
 *  the market gap and the movement answer, and a fall of 200 is as interesting
 *  as a rise of 200. A value that could not be computed sorts last: it is
 *  unknown, not zero, and parking it in the middle would read as "unchanged". */
function byMagnitude(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return Math.abs(b) - Math.abs(a);
}

export function sortTraderItems(
  rows: TraderItemDetail[], sort: TraderSort,
): TraderItemDetail[] {
  return [...rows].sort((a, b) => {
    if (sort === "mode") return byText(a.mode, b.mode) || byText(a.item, b.item);
    if (sort === "vsMarket") return byMagnitude(vsMarket(a), vsMarket(b)) || byText(a.item, b.item);
    if (sort === "moved") return byMagnitude(moved(a), moved(b)) || byText(a.item, b.item);
    return byText(a.item, b.item) || byText(a.mode, b.mode);
  });
}

export function filterTraderItems(
  rows: TraderItemDetail[], needle: string,
): TraderItemDetail[] {
  const term = needle.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter((row) => row.item.toLowerCase().includes(term));
}

/** What makes two rows the same thing on offer.
 *
 *  The item and the mode are not enough. Worn gear is listed at an enchant
 *  level and a weapon with an element and an ability, and a +6 is a different
 *  good at a different price — which is why the table now shows one row per
 *  combination rather than one per item. `${item_id}-${mode}` was the React
 *  key until then, and with split rows it renders duplicates in silence.
 *
 *  `enchant || 0` mirrors server/lib/variation.py: +0 and "no enchant" are one
 *  absence, and so are "" and null, so a row keys the same however it arrived. */
export function listingKey(
  row: { item_id: number; mode: string; enchant: number | null;
         element: string | null; ability: string | null },
): string {
  return [
    row.item_id, row.mode, row.enchant || 0, row.element || "", row.ability || "",
  ].join(" ");
}
